import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
	Container,
	Paper,
	Stepper,
	Step,
	StepLabel,
	Box,
	Typography,
	Alert,
	CircularProgress,
} from "@mui/material";
import FileUpload from "./FileUpload";
import ZoneMapping from "./ZoneMapping";
import FacilitySelection from "./FacilitySelection";
import RunAllocation from "./RunAllocation";
import Results from "./Results";
import { getDefaultZoneMapping, updateJob } from "./services/api";

const steps = [
	"Upload File",
	"Configure Zone Mapping",
	"Select Facilities",
	"Run Allocation",
	"Results",
];

function WHAllocation() {
	const location = useLocation();
	const navigate = useNavigate();
	const [activeStep, setActiveStep] = useState(0);
	const [uploadedFile, setUploadedFile] = useState(null);
	const [regions, setRegions] = useState([]);
	const [zoneMapping, setZoneMapping] = useState({});
	const [facilityMapping, setFacilityMapping] = useState({});
	const [selectedFacilities, setSelectedFacilities] = useState([]);
	const [allocationResults, setAllocationResults] = useState(null);
	const [error, setError] = useState("");
	const [loading, setLoading] = useState(false);
	const [nextLoading, setNextLoading] = useState(false);
	const [savedZoneMapping, setSavedZoneMapping] = useState(null);
	const [savedFacilityMapping, setSavedFacilityMapping] = useState(null);
	const [savedSelectedFacilities, setSavedSelectedFacilities] = useState(null);

	useEffect(() => {
		const isResuming = Boolean(location.state?.resumeJob);
		getDefaultZoneMapping()
			.then((res) => res.data)
			.then((data) => {
				if (data.success && !isResuming) {
					setZoneMapping(data.zone_mapping);
				}
			})
			.catch((err) => console.error("Error loading zone mapping:", err));
	}, []);

	const handleFileUploaded = (fileData) => {
		setUploadedFile(fileData);
		setRegions(fileData.regions || []);

		const fileZones = (fileData.regions || []).map((r) => r.toUpperCase());
		const updatedMapping = { ...zoneMapping };
		fileZones.forEach((zone) => {
			if (!updatedMapping[zone]) {
				updatedMapping[zone] = ["GGN", "JIGANI", "MALUR", "WB", "MH"];
			}
		});
		setZoneMapping(updatedMapping);

		setActiveStep(1);
		setError("");
	};

	const zoneMappingChanged = () => {
		if (savedZoneMapping == null) return true;
		return JSON.stringify(zoneMapping) !== JSON.stringify(savedZoneMapping);
	};
	const facilityMappingChanged = () => {
		if (savedFacilityMapping == null) return true;
		return JSON.stringify(facilityMapping) !== JSON.stringify(savedFacilityMapping);
	};
	const selectedFacilitiesChanged = () => {
		if (savedSelectedFacilities == null) return true;
		const a = [...(selectedFacilities || [])].sort();
		const b = [...(savedSelectedFacilities || [])].sort();
		return JSON.stringify(a) !== JSON.stringify(b);
	};

	const handleNext = async () => {
		const jobId = uploadedFile?.job_id;
		const shouldPatchStep1 = jobId && activeStep === 1 && zoneMappingChanged();
		const step2Changed = selectedFacilitiesChanged() || facilityMappingChanged();
		const shouldPatchStep2 = jobId && activeStep === 2 && step2Changed;
		if (shouldPatchStep1 || shouldPatchStep2) {
			setNextLoading(true);
			try {
				if (shouldPatchStep1) {
					await updateJob(jobId, { zone_mapping: zoneMapping });
					setSavedZoneMapping(zoneMapping);
				} else if (shouldPatchStep2) {
					await updateJob(jobId, {
						selected_facilities: selectedFacilities,
						facility_mapping: facilityMapping,
					});
					setSavedSelectedFacilities(selectedFacilities);
					setSavedFacilityMapping(facilityMapping);
				}
			} catch (err) {
				console.error("Failed to save job progress:", err);
				setNextLoading(false);
				return;
			}
			setNextLoading(false);
		}
		setActiveStep((prev) => prev + 1);
		setError("");
	};

	const handleBack = () => {
		setActiveStep((prev) => prev - 1);
		setError("");
	};

	const handleZoneMappingChange = (updatedMapping) => {
		setZoneMapping(updatedMapping);
	};

	const handleFacilitySelectionChange = (facilities, mapping) => {
		setSelectedFacilities(facilities);
		if (mapping != null) setFacilityMapping(mapping);
	};

	const handleAllocationComplete = (results) => {
		setAllocationResults(results);
		setActiveStep(4);
		setError("");
	};

	const handleResumeJob = (job) => {
		const fileData = {
			job_id: job.job_id,
			filename: job.input_filename,
			filepath: job.input_filepath,
			regions: job.regions || [],
			total_records: job.total_records || 0,
		};
		setUploadedFile(fileData);
		setRegions(job.regions || []);
		const fileZones = (job.regions || []).map((r) => (typeof r === "string" ? r : String(r)).toUpperCase());
		const defaultWarehouses = ["GGN", "JIGANI", "MALUR", "WB", "MH"];
		if (job.zone_mapping && Object.keys(job.zone_mapping).length > 0) {
			const updated = { ...job.zone_mapping };
			fileZones.forEach((zone) => {
				if (!updated[zone] || !Array.isArray(updated[zone])) {
					updated[zone] = defaultWarehouses;
				}
			});
			setZoneMapping(updated);
		} else {
			setZoneMapping((prev) => {
				const updated = { ...prev };
				fileZones.forEach((zone) => {
					if (!updated[zone]) updated[zone] = defaultWarehouses;
				});
				return updated;
			});
		}
		if (job.selected_facilities && Array.isArray(job.selected_facilities) && job.selected_facilities.length > 0) {
			setSelectedFacilities(job.selected_facilities);
		}
		if (job.facility_mapping && Object.keys(job.facility_mapping).length > 0) {
			setFacilityMapping(job.facility_mapping);
		}
		setSavedZoneMapping(job.zone_mapping && Object.keys(job.zone_mapping).length > 0 ? job.zone_mapping : null);
		setSavedFacilityMapping(
			job.facility_mapping && Object.keys(job.facility_mapping).length > 0 ? job.facility_mapping : null
		);
		setSavedSelectedFacilities(
			job.selected_facilities && job.selected_facilities.length > 0 ? job.selected_facilities : null
		);

		// Jump to the furthest completed step, or back to step 1 for failed jobs
		const hasZoneMapping = job.zone_mapping && Object.keys(job.zone_mapping).length > 0;
		const hasFacilities = job.selected_facilities && job.selected_facilities.length > 0;
		let resumeStep = 1;
		if (job.stage === "FAILED") {
			resumeStep = 3; // Go straight to run allocation so user can retry
		} else if (hasZoneMapping && hasFacilities) {
			resumeStep = 3; // Everything configured — go to run allocation
		} else if (hasZoneMapping) {
			resumeStep = 2; // Zone done — go to facility selection
		}
		setActiveStep(resumeStep);
		setError("");
	};

	useEffect(() => {
		const resumeJob = location.state?.resumeJob;
		if (resumeJob) {
			handleResumeJob(resumeJob);
			navigate(location.pathname, { replace: true, state: {} });
		}
	}, [location.state]);

	const renderStepContent = (step) => {
		switch (step) {
			case 0:
				return (
					<FileUpload
						onFileUploaded={handleFileUploaded}
						onError={setError}
						loading={loading}
						setLoading={setLoading}
					/>
				);
			case 1:
				return (
					<ZoneMapping
						zoneMapping={zoneMapping}
						regions={regions}
						onChange={handleZoneMappingChange}
						onNext={handleNext}
						onBack={handleBack}
						nextLoading={nextLoading}
					/>
				);
			case 2:
				return (
					<FacilitySelection
						facilityMapping={facilityMapping}
						selectedFacilities={selectedFacilities}
						onChange={handleFacilitySelectionChange}
						onNext={handleNext}
						onBack={handleBack}
						nextLoading={nextLoading}
					/>
				);
			case 3:
				return (
					<RunAllocation
						uploadedFile={uploadedFile}
						zoneMapping={zoneMapping}
						facilityMapping={facilityMapping}
						selectedFacilities={selectedFacilities}
						onComplete={handleAllocationComplete}
						onError={setError}
						loading={loading}
						setLoading={setLoading}
						onBack={handleBack}
					/>
				);
			case 4:
				return <Results results={allocationResults} onBack={handleBack} />;
			default:
				return null;
		}
	};

	return (
		<Box sx={{ minHeight: "100vh", py: 4 }}>
			<Container maxWidth="lg">
				<Paper elevation={3} sx={{ p: 4, borderRadius: 2, border: "1px solid", borderColor: "divider" }}>
					<Typography variant="h4" component="h1" align="center" gutterBottom sx={{ fontWeight: "bold", mb: 2 }}>
						AXIS
					</Typography>

					<Typography variant="body1" align="center" color="text.secondary" sx={{ mb: 4 }}>
						Upload your requirements file and configure allocation settings
					</Typography>

					<Stepper activeStep={activeStep} sx={{ mb: 4 }}>
						{steps.map((label) => (
							<Step key={label}>
								<StepLabel>{label}</StepLabel>
							</Step>
						))}
					</Stepper>

					{error && (
						<Alert severity="error" sx={{ mb: 3 }} onClose={() => setError("")}>
							{error}
						</Alert>
					)}

					{loading && (
						<Box display="flex" justifyContent="center" mb={3}>
							<CircularProgress />
						</Box>
					)}

					<Box sx={{ minHeight: "400px" }}>{renderStepContent(activeStep)}</Box>
				</Paper>
			</Container>
		</Box>
	);
}

export default WHAllocation;
